import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { migrationApi, type MigrationCaseLinkTarget } from '@/lib/api';
import { Loader2, Mail, Phone, UserPlus } from 'lucide-react';

type Props = {
  open: boolean;
  caseId: number | null;
  onOpenChange: (open: boolean) => void;
  onLinked?: () => Promise<void> | void;
};

type SubprofileChoice =
  | { kind: 'existing'; id: string }
  | { kind: 'new' };

const formatBirthDate = (value?: string | null): string => {
  if (!value) return '';
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('de-DE');
};

export default function MigrationCaseLinkModal({ open, caseId, onOpenChange, onLinked }: Readonly<Props>) {
  const [target, setTarget] = useState<MigrationCaseLinkTarget | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [choice, setChoice] = useState<SubprofileChoice>({ kind: 'new' });
  const [newFirst, setNewFirst] = useState('');
  const [newLast, setNewLast] = useState('');
  const [newBirth, setNewBirth] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !caseId) return;
    let active = true;
    setTarget(null);
    setLoadError(null);
    setSubmitError(null);
    setIsLoading(true);
    migrationApi
      .getCaseLinkTarget(caseId)
      .then((res) => {
        if (!active) return;
        setTarget(res);
        if (res.matched) {
          // Vorauswahl: existierendes Schülerprofil mit identischem Namen, sonst „neues Schülerprofil"
          const suggestion = res.suggested_new_subprofile;
          const existing = res.wp_user?.subprofiles ?? [];
          const matchByName = suggestion
            ? existing.find((s) =>
                `${s.firstName} ${s.lastName}`.trim().toLowerCase() ===
                `${suggestion.firstName} ${suggestion.lastName}`.trim().toLowerCase()
              )
            : null;
          if (matchByName) {
            setChoice({ kind: 'existing', id: matchByName.id });
          } else {
            setChoice({ kind: 'new' });
          }
          setNewFirst(suggestion?.firstName ?? '');
          setNewLast(suggestion?.lastName ?? '');
          setNewBirth(suggestion?.birthDate ?? '');
        }
      })
      .catch((e) => {
        if (active) setLoadError(e instanceof Error ? e.message : 'Verknüpfungs-Daten konnten nicht geladen werden');
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open, caseId]);

  const handleSubmit = async () => {
    if (!caseId || !target?.matched || !target.wp_user) return;
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const body: Parameters<typeof migrationApi.linkCaseToCustomer>[1] = {
        wp_user_id: target.wp_user.id,
      };
      if (choice.kind === 'existing') {
        body.subprofile_id = choice.id;
      } else {
        if (!newFirst.trim() && !newLast.trim()) {
          setSubmitError('Für ein neues Schülerprofil ist Vor- oder Nachname erforderlich.');
          setIsSubmitting(false);
          return;
        }
        body.new_subprofile = {
          firstName: newFirst.trim(),
          lastName: newLast.trim(),
          birthDate: newBirth.trim() || null,
        };
      }
      await migrationApi.linkCaseToCustomer(caseId, body);
      if (onLinked) await onLinked();
      onOpenChange(false);
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'Verknüpfung fehlgeschlagen');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = (() => {
    if (!target?.matched || isSubmitting || isLoading) return false;
    if (choice.kind === 'existing') return choice.id !== '';
    return newFirst.trim() !== '' || newLast.trim() !== '';
  })();

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      );
    }
    if (loadError) {
      return <p className="rounded-md border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">{loadError}</p>;
    }
    if (!target?.matched) {
      const reason = target?.reason;
      const msg =
        reason === 'no_email_in_snapshot'
          ? 'Im Migrations-Snapshot ist keine E-Mail-Adresse hinterlegt, die als Anker für die Verknüpfung dienen könnte.'
          : reason === 'no_user_match'
            ? `Zur E-Mail ${target?.email ?? '—'} existiert noch kein Kundenaccount. Lege den Datensatz im Vertragsanpassungs-Wizard neu an.`
            : 'Es konnte kein passender Kundenaccount ermittelt werden.';
      return (
        <p className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200">{msg}</p>
      );
    }

    const user = target.wp_user;
    if (!user) {
      return <p className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200">Kundenaccount-Daten konnten nicht geladen werden.</p>;
    }
    const subs = user.subprofiles ?? [];

    return (
      <div className="space-y-5">
        {/* Match-Karte */}
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-4">
          <div className="text-[10px] uppercase tracking-widest text-primary/80">
            Passender Kundenaccount
          </div>
          <div className="mt-1 text-base font-semibold">{user.name || '—'}</div>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
            {user.email ? (
              <span className="inline-flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5" />
                {user.email}
              </span>
            ) : null}
            {user.phone ? (
              <span className="inline-flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5" />
                {user.phone}
              </span>
            ) : null}
          </div>
        </div>

        {/* Schülerprofil-Auswahl */}
        <div>
          <div className="mb-2 text-sm font-medium">Schülerprofil wählen</div>
          <div className="space-y-2">
            {subs.map((s) => {
              const checked = choice.kind === 'existing' && choice.id === s.id;
              return (
                <label
                  key={s.id}
                  className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors ${
                    checked ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40'
                  }`}
                >
                  <input
                    type="radio"
                    name="subprofile-choice"
                    className="mt-1"
                    checked={checked}
                    onChange={() => setChoice({ kind: 'existing', id: s.id })}
                  />
                  <div className="flex-1">
                    <div className="font-medium">{s.name || `${s.firstName} ${s.lastName}`.trim() || `Schülerprofil #${s.index}`}</div>
                    <div className="text-xs text-muted-foreground">
                      {s.birthDate ? `geb. ${formatBirthDate(s.birthDate)} · ` : ''}
                      ID {s.id}
                      {s.relationshipType ? ` · ${s.relationshipType}` : ''}
                    </div>
                  </div>
                </label>
              );
            })}

            {/* "Neues Schülerprofil" Option */}
            <label
              className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors ${
                choice.kind === 'new' ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40'
              }`}
            >
              <input
                type="radio"
                name="subprofile-choice"
                className="mt-1"
                checked={choice.kind === 'new'}
                onChange={() => setChoice({ kind: 'new' })}
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 font-medium">
                  <UserPlus className="h-3.5 w-3.5" />
                  Neues Schülerprofil anlegen
                </div>
                {choice.kind === 'new' ? (
                  <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
                    <Input
                      placeholder="Vorname"
                      value={newFirst}
                      onChange={(e) => setNewFirst(e.target.value)}
                    />
                    <Input
                      placeholder="Nachname"
                      value={newLast}
                      onChange={(e) => setNewLast(e.target.value)}
                    />
                    <Input
                      type="date"
                      value={newBirth}
                      onChange={(e) => setNewBirth(e.target.value)}
                    />
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground">
                    Vorausgefüllt mit den Schüler-Daten aus dem Migrations-Snapshot.
                  </div>
                )}
              </div>
            </label>
          </div>
        </div>

        {submitError ? (
          <p className="rounded-md border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">{submitError}</p>
        ) : null}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-2xl flex-col">
        <DialogHeader>
          <DialogTitle>Datensatz verknüpfen</DialogTitle>
          <DialogDescription>
            Wir haben einen passenden Kundenaccount gefunden. Wähle das richtige Schülerprofil aus oder lege ein neues an.
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto py-2">{renderBody()}</div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Abbrechen
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={!canSubmit} loading={isSubmitting}>
            Verknüpfen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
